import { Inject, Injectable, forwardRef } from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiOptionsModel }                 from '../api/models/api.options';
import { ApiService }                      from '../api/api.service';
import { CustomerModel }                   from '../customers/models/customer';
import { MessagesService }                 from '../messages/messages.service';
import { ProductsAdvancedSearchComponent } from './products.advanced-search.component';
import { ProductGroupModel }               from '../company-lists/models/product-group';
import { StreamSearchService }             from '../api/stream-search.service';
import { VerboseStreamResponseModel }      from '../api/models/stream.verbose-response';

import AshdownProductSearchResultsHtml from './templates/ashdown-products.search-results.html';
import ProlinkProductSearchResultsHtml from './templates/prolink-products.search-results.html';
import RepcoProductSearchResultsHtml   from './templates/repco-products.search-results.html';

@Injectable({
  providedIn: 'root'
})
export class ProductSearchService {
  // Flag to block quick search (when stream has already resolved)
  _blockQuickSearch = false;
  products          = [];
  _stream           = undefined;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService         : ApiService,
    @Inject(forwardRef(() => MessagesService)) private messagesService    : MessagesService,
    private modalService       : NgbModal,
    @Inject(forwardRef(() => StreamSearchService)) private streamSearchService: StreamSearchService,
    private translateService   : TranslateService
  ) {}

  populate ( product : any, row : any, isNewProduct : boolean, items : Array<any>, customer ?: any ) : void {
    const item = items[row.rowIndex];

    if (item) {
      item.description  = product.description;
      item.item_code    = product.item_code;
      item.product_id   = product.id;
      item.product_type = product.product_type;
      item.unit_cost    = product.cost;
      item.unit_price   = product.retail_price;

      if (!isNewProduct) {
        item.gst_free    = product.gst_free;
        item.note        = product.comment;
        item.quantity    = 1;
      }
    }
  }

  advancedSearch ( product : any ) : Promise<any> {
    let groups : Array<ProductGroupModel>;
    let types  : Array<any>;

    return this.apiService.get('/product_groups')
    .then(( results : Array<ProductGroupModel> ) => {
      groups = results;

      return this.apiService.get('/system/product_types/', null, 'productTypes');
    })
    .then(( results : Array<any> ) => {
      types = results;

      return this.apiService.get('/products/product_list', [
        product.item_code,
        0,
        5,
      ], null, { skipEncoding: true });
    })
    .then(( products : Array<any> ) => {
      const modal = this.modalService.open(ProductsAdvancedSearchComponent, {
        size       : 'lg',
        windowClass: 'product-search-modal'
      });

      modal.componentInstance.groups   = groups;
      modal.componentInstance.product  = product;
      modal.componentInstance.products = products;
      modal.componentInstance.types    = types;

      return modal.result;
    });


  }

  deleteItems ( items : any, embed : any ) : Promise<any> {
    return this.apiService.post('/bom_items', items, embed);
  }

  getAdjustedCost ( recordType : 'event' | 'invoice' = 'invoice', customer : CustomerModel, item : any ) : Promise<any> {
    const route = recordType === 'event'
      ? '/event_items/adjusted_cost'
      : '/invoice_items/adjusted_cost';

    return this.apiService
    .get(route, [ customer.id, item.id ])
    .catch(() => item.retail_price);
  }

  getBomItems ( recordType : 'bom' | 'event' | 'invoice' = 'invoice', product : any, params : any ) : Promise<any> {
    let route : string;

    switch (recordType) {
      case 'bom':
        route = '/products/list_bom_items';
        break;
      case 'event':
        route = '/events/create_event_items_from_bom';
        break;
      case 'invoice':
      default:
        route = '/invoice/create_invoice_items_from_bom';
        break;
    }

    if ( ( params.length <= 3 && params[ 1 ] ) || params.length > 3 ) {
      return this.apiService.get(route, params || product.id);
    }
    else {
      this.messagesService.show(this.translateService.instant('CUSTOMER_SPACE.NO_CUSTOMER_ATTACHED_BUNDLE'), 'error');

      return Promise.reject();
    }
  }

  getLaborProducts ( value : string | number ) : Promise<any> {
    return this.apiService.get('/products/product_labor_list', [
      value,
      0,
      100
    ]);
  }

  getSearchTemplate ( ashdown : any, repco : any, prolink : any ) : string | null {
    if ( ashdown.vendor ) {
      return AshdownProductSearchResultsHtml;
    }
    else if ( repco.vendor ) {
      return RepcoProductSearchResultsHtml;
    }
    else if ( prolink.vendor ) {
      return ProlinkProductSearchResultsHtml;
    }
    else {
      return null;
    }
  }

  /**
   * When the user tabs out of the field, we
   * want to do a quick lookup on the typed value.
   * If we get a matching product, select it and
   * cancel the stream
   */
  quickSearch ( typed : string ) : Promise<any> {
    // Cancel if quick search is blocked or no value is typed
    if ( this._blockQuickSearch || !typed ) {
      this._blockQuickSearch = false;

      return Promise.reject();
    }

    return this.apiService
    .get('/product/find_by_item_code', typed)
    .then(( product ) => {
      // Since quick search is resolved now, cancel any stream
      if ( this._stream ) {
        this._stream.close();

        this._stream = undefined;
      }

      return product;
    });
  }

  /**
   * Performs an eventsource stream with
   * the typed value. Different to above as it gets the qty available
   */
  reservedStockSearch ( typed : string ) : Promise<any> {
    // Quick Search is available until this call resolves
    this._blockQuickSearch = false;

    const search = this.streamSearchService.search('products_reserved', typed, {
      verbose: true
    } as ApiOptionsModel) as VerboseStreamResponseModel;

    // Save the stream for reference
    this._stream = search.stream;

    return search.promise
    .then(( products : Array<any> ) => {
      products.push({
        item_code: typed,
        last     : true
      });

      return this.products.concat(products);
    })
    .finally(() => {
      // Now that it has resolved, block any quick searches
      this._blockQuickSearch = true;
    });
  }

  search ( typed : string ) : Promise<any> {
    // Quick Search is available until this call resolves
    this._blockQuickSearch = false;

    const search = this.streamSearchService.search('products', typed, {
      verbose: true
    } as ApiOptionsModel) as VerboseStreamResponseModel;

    // Save the stream for reference
    this._stream = search.stream;

    return search.promise
    .then(( products : Array<any> ) => {
      products.push({
        item_code: typed,
        last     : true
      });

      return this.products.concat(products);
    })
    .finally(() => {
      // Now that it has resolved, block any quick searches
      this._blockQuickSearch = true;
    });
  }

  searchBranchProducts ( company_id : string, typed : string ) : Promise<any> {
    return this.apiService.post('/product/branch_find', {
      company_id: company_id,
      item_code : typed
    });
  }

  searchDealerBranchProducts ( company_id : string, typed : string ) : Promise<any> {
    return this.apiService.post('/product/branch_find_on_dealer', {
      company_id: company_id,
      item_code : typed
    });
  }

  updatePricing ( items : any, embed : any ) : Promise<any> {
    return this.apiService.post('/update_pricing', items, embed);
  }
}
